import React, {Fragment, lazy, Suspense, useEffect, useState} from 'react';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    makeStyles,
    Modal,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@material-ui/core";
import {Alert} from '@material-ui/lab';

import useStyles from '../styles/styles';
import {useLazyQuery, useQuery} from "@apollo/react-hooks";
import {gql} from "apollo-boost";
import {format} from 'date-fns';
import {navigate} from '@reach/router';

import LeadNavigationMenu from "../components/Leads/LeadNavigationMenu";
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import WarningIcon from '@material-ui/icons/Warning';
import LaunchIcon from '@material-ui/icons/Launch';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import CurrencyField from "../components/Fields/CurrencyField";
import PhoneField from "../components/Fields/PhoneField";
import SensitiveField from "../components/Fields/SensitiveField";
import BankInformation from "../components/Leads/BankInformation";
import VehicleInformation from "../components/Leads/VehicleInformation";
import LoanResultInformation from "../components/Leads/LoanResultInformation";
import LoanResponseInformation from "../components/Leads/LoanResponseInformation";
import theme from '../styles/theme';
import ParseResponse from '../utils/ParseResponse';
import ExperimentRecords from "../components/Leads/ExperimentRecords";
import setDate from '../utils/setDate';
import convertToCurrency from '../utils/convertToCurrency';
import dashes from 'add-dashes-to-uuid';
import ErrorBoundary from "../components/ErrorBoundary";
import IdentifyEventsTable from "../components/Leads/IdentifyEventsTable";
import SignatureBlocksTable from "../components/Leads/SignatureBlocksTable";
import HostedSiteInformation from "../components/Leads/HostedSiteInformation";

const CloudwatchLogsTable = lazy(() => import("../components/Leads/CloudwatchLogsTable"));
const CampaignFilterResultsTable = lazy(() => import("../components/Leads/CampaignFilterResultsTable"));
const LoanRedirectDataTable = lazy(() => import("../components/Leads/LoanRedirectDataTable"));
const MarketingRedirectTable = lazy(() => import("../components/Leads/MarketingRedirectTable"));

const useBoxStyles = makeStyles({
    root: {
        '& .MuiPaper-elevation1': {
            height: '400px',
            overflow: 'scroll'
        }
    },
});

const Lead = ({leadId}) => {
    const id = leadId.includes('-') ? leadId : dashes(leadId);
    const classes = useStyles();
    const boxClasses = useBoxStyles();
    const [selectedTab, setSelectedTab] = useState(0);
    const [isLogOpen, setLogOpen] = useState(false);
    const [modalData, setModalData] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadComponents, setLoadComponents] = useState(false);
    const openModal = info => {
        setModalData(info);
        setIsModalOpen(true);
    };
    const closeModal = () => {
        setIsModalOpen(false);
    };

    const {loading, error, data} = useQuery(gql`
        query LeadPage($leadId: String!) {
            loanRequest(id: $leadId) {
                id
                createdAt
                clientIpAddress
                everflowTransactionId
                userAgent
                pingTree
                dateCreatedEffective
                timeout
                sourceKey
                sourceLeadId
                sourceSessionId
                sourceSubCode1
                sourceSubCode1Hash
                sourceSubCode2
                sourceSubCode2Hash
                sourceSubCode3
                isTestApp
                trafficSource {
                    id
                    name
                    medium
                    origin
                    channel
                    legacyZoneId
                    legacyPromoId
                    legacyOfferId
                    affiliate {
                        id
                        name
                        legacyPublisherId
                    }
                    payoutStrategyEnum
                }

                minimumLoanAmount
                maximumLoanAmount
                minimumPrice

                # Person data
                firstName
                middleName
                lastName
                primaryEmail
                primaryPhone
                homePhone
                mobilePhone
                workPhone
                workExt
                dateOfBirth
                military
                ssn
                ssnLastFour
                stateIdNumber
                stateIdNumberHash
                stateIdIssued
                verifiedCustomer

                # HomeAddress
                homeStreetAddress
                homeStreetAddress2
                homeCity
                homeState
                homeZip
                residenceType
                residenceLengthMonths

                # Employment Info
                employerName
                employerLengthMonths
                employmentType
                employmentSubType

                # Income Info
                payFrequency
                monthlyIncome
                nextPayDate
                secondPayDate
                payType

                # Bank Info
                bankName
                bankRouting
                bankAccount
                bankAccountType
                monthsAtBank
                
                # Vehicle Info
                vehicleInfo {
                    vehicleMake
                    vehicleModel
                    vehicleTrim
                    vehicleYear
                    vehicleMileage
                }
                
                titleAsCollateral
                titleClear
                
                referringSiteUrl

                loanResponse {
                    id
                    createdAt
                    publisherTimeout
                    billed
                    payout
                    status
                    responseTime
                    responseRendered
                    campaignId
                    portfolioId
                    servicerId
                    ampOfferAmount
                    ampOfferExpiration
                    ampOfferToken
                }

                loanResult {
                    id
                    requestReceivedAt
                    processCompletedAt
                    processTime
                    portfolioId
                    status
                    billed
                    payout
                    redirectUrl
                    campaign {
                        id
                        name
                    }
                    servicer {
                        id
                        name
                    }
                    portfolio {
                        id
                        name
                    }
                }

                loanRedirect {
                    id
                    destinationUrl
                    clientIpAddress
                    clientIpVersion
                    userAgent
                    dateCreated
                    elapsedSeconds
                }

                marketingRedirect {
                    id
                    loanRequestId
                    localUrl
                    popUnderUrl
                }

                leadScores {
                    id
                    createdAt
                    grade
                    score
                }

                campaignDecisionLog {
                    id
                    createdAt
                    timestamp
                    requestId
                    servicer
                    portfolio
                    campaignId
                    campaignType
                    status
                    publisherTimeout
                    pricePoint
                    billable
                    payable
                    request
                    response
                    result
                    requestTimestamp
                    responseTimestamp
                    statusCode
                    tierMin
                    type
                    elapsedTime
                    elapsedTimeSeconds
                    vendorMessage
                    vendorSessionId
                    strategy
                }

                bids {
                    applicationId
                    campaignDecisionId
                    campaignId
                    createdAt
                    expirationInSeconds
                    expires
                    id
                    identityId
                    leadId
                    loanRequestId
                    modifiedAt
                    originalTimestamp
                    pricePoint
                    redeemedRequestId
                    redeemedTimestamp
                    status
                    token
                }

                campaignMinPrice {
                    campaignId
                    createdAt
                    id
                    minPrice
                    requestId
                }

                performanceSummaryData {
                    id
                    timeToFirstCampaignHttpRequest
                    totalSynchronousPostingTime
                }

                experiments {
                    id
                    createdAt
                    experiment
                    variant
                }
                
                sourceSwap {
                    dateCreated
                    description
                    finalTrafficSourceId
                    loanRequestId
                    originalTrafficSourceId
                    swapperName
                    originalPromoId
                    finalPromoId
                    originalTrafficSourceName
                    finalTrafficSourceName
                    originalTrafficSourceAffiliateName
                    finalTrafficSourceAffiliateName
                }
                
                payoutStrategySwap {
                    originalStrategy
                    swappedStrategy
                    reason
                }
                
                blockActions {
                    id
                    loanRequestId
                    blockActionTaken
                    blockReason
                    dateCreated
                    signatureBlock {
                        blockReason
                        blockValue
                        dateCreated
                        expires
                        filterField
                        filterType
                        id
                        signatureBlockSource
                        trafficSourceStateGroupId
                    }
                }
            }
        }
    `, {
        variables: {
            leadId: id,
        }
    });

    const {
        loading: hostedSiteDataLoading,
        error: hostedSiteDataError,
        data: hostedSiteData,
        refetch: hostedSiteDataRefetch
    } = useQuery(gql`
        query HostedSiteData($apiRequestId: UUID!) {
            hostedSiteData(apiRequestId: $apiRequestId) {
                formId,
                websiteName,
                otpVerified,
                otpChoice,
                otpAttempts,
                resumeResult,
                isReturning,
                returningDetected,
                returningDisposition,
                neoBankName,
                neoBankChangeType,
                routingNumberBankName,
                neoBankRoutingNumberBankName,
                neoBankRoutingNumberChangeType
            }
        }
    `, {
        variables: {
            apiRequestId: id,
        },
        context: {
            clientName: "kgraphql"
        }
    });

    const [loanRequestLogQuery, {
        loading: loanRequestLogLoading,
        error: loanRequestLogError,
        data: loanRequestLogData,
        refetch: loanRequestLogRefetch
    }] = useLazyQuery(gql`
        query LoanRequestLog($loanRequestId: UUID!) {
            loanRequestLog(loanRequestId: $loanRequestId) {
                createdAt
                id
                message
                olpDecision
                olpSessionId
                request
                response
                statusCode
                uri
                error
            }
        }`, {
        context: {
            clientName: "kgraphql"
        }
    });

    const onClickLoadLog = () => {
        if (data && !loanRequestLogData && !loanRequestLogError) {
            loanRequestLogQuery({
                variables: {
                    loanRequestId: data.loanRequest.id,
                }
            });
        } else if (data) {
            loanRequestLogRefetch({
                variables: {
                    loanRequestId: data.loanRequest.id,
                }
            });
        }
    }

    useEffect(() => {
        if (loanRequestLogData) {
            console.log('loanRequestLogData', loanRequestLogData)
        }
    }, [loanRequestLogData])

    useEffect(() => {
        setTimeout(() => {
            setLoadComponents(true);
        }, [3000])
    }, [data]);

    if (loading) {
        return (
            <Backdrop className={classes.backdrop} open={loading}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        )
    }

    if (error) {
        return (
            <Paper className={classes.paper}>
                <Alert severity="error">Well, this in embarrassing&hellip; something went wrong.</Alert>
                <p>
                    <a href="/#" onClick={(e) => {
                        e.preventDefault();
                        navigate('/info/leads');
                    }}>Click here to view all leads.</a>
                </p>
            </Paper>
        )
    }

    if (!data || !data.loanRequest) {
        return (
            <>
                <Typography component="h2">
                    LoanRequest data is invalid
                </Typography>
                <pre>${JSON.stringify(data, null, 2)}</pre>
            </>
        )
    }

    data.loanRequest = data.loanRequest || {}

    data.loanRequest.createdAt = setDate(data.loanRequest.createdAt)
    data.loanRequest.dateOfBirth = new Date(data.loanRequest.dateOfBirth)
    data.loanRequest.nextPayDate = new Date(data.loanRequest.nextPayDate)
    data.loanRequest.secondPayDate = new Date(data.loanRequest.secondPayDate)

    // console.log(data.loanRequest.blockActions)

    const CreateOlpLink = ({olpSessionId}) => {
        if (olpSessionId) {
            const env = process.env.NODE_ENV;

            if (env === 'development') {
                return (
                    <a target="_blank"
                       rel="noopener noreferrer"
                       href={`http://test.tools.partnerweekly.com/application/index.php?application_id=${olpSessionId}&campaign_info_id=&mode=rc`}>
                        {olpSessionId}
                    </a>
                );
            } else {
                return (
                    <a target="_blank"
                       rel="noopener noreferrer"
                       href={`http://secure.tools.partnerweekly.com/application/index.php?application_id=${olpSessionId}&campaign_info_id=&mode=slave`}>
                        {olpSessionId}
                    </a>
                );
            }
        }

        return 'N/A';
    };

    const handleOpen = (request, response) => {
        setModalData({
            request,
            response
        });
        setLogOpen(true);
    };

    const handleClose = () => {
        setModalData({});
        setLogOpen(false);
    };

    const handleTabClick = (e, val) => {
        setSelectedTab(val);
    };

    const signatureBlock = () => {
        return data.loanRequest.blockActions && data.loanRequest.blockActions.length ? (
            <div style={{backgroundColor: theme.palette.warning.main}} className={classes.signatureNotification}
                 onClick={(e) => {
                     window.scrollTo(0, document.querySelector("#signature-block-table").offsetTop);
                 }}>
                <WarningIcon style={{fontSize: '300%', marginRight: theme.spacing(1)}}/>
                <Typography background={"secondary"} variant="h2" component="h2"
                            className={classes.monospace + " " + classes.blockSignature} id={"page-top"}
                > SIGNATURE BLOCKED </Typography>
            </div>

        ) : <></>
    }

    return (
        <>
            <Typography variant="h2" component="h2" className={classes.monospace} id={"page-top"}>
                {id}
            </Typography>
            {signatureBlock()}
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Created At" value={data.loanRequest.createdAt || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="IP Address" value={data.loanRequest.clientIpAddress || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="User Agent" value={data.loanRequest.userAgent || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField label="Traffic Source" value={data.loanRequest.trafficSource.name || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Minimum Price"
                                   value={data.loanRequest.minimumPrice !== null ? convertToCurrency(data.loanRequest.minimumPrice) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Minimum Loan Amount"
                                   value={data.loanRequest.minimumLoanAmount !== null ? convertToCurrency(data.loanRequest.minimumLoanAmount) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Maximum Loan Amount"
                                   value={data.loanRequest.maximumLoanAmount !== null ? convertToCurrency(data.loanRequest.maximumLoanAmount) : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Ping Tree" value={data.loanRequest.pingTree || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Channel" value={data.loanRequest.trafficSource.channel || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Origin" value={data.loanRequest.trafficSource.origin || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Medium" value={data.loanRequest.trafficSource.medium || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Affiliate" value={data.loanRequest.trafficSource.affiliate.name || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={8} lg={4}>
                        <TextField label="Payout Strategy"
                                   value={data.loanRequest.payoutStrategySwap?.swappedStrategy || data.loanRequest.trafficSource.payoutStrategyEnum || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={2}>
                        <TextField label="Affiliate Legacy Publisher ID"
                                   value={data.loanRequest.trafficSource.affiliate.legacyPublisherId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Zone ID" value={data.loanRequest.trafficSource.legacyZoneId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Promo ID" value={data.loanRequest.trafficSource.legacyPromoId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Legacy Offer ID" value={data.loanRequest.trafficSource.legacyOfferId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Timeout" value={data.loanRequest.timeout || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={1}>
                        <TextField label="Is Test App" value={data.loanRequest.isTestApp || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={5}>
                        <TextField label="Source Key" value={data.loanRequest.sourceKey || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Source Lead Id" value={data.loanRequest.sourceLeadId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Source Session Id" value={data.loanRequest.sourceSessionId || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 1" value={data.loanRequest.sourceSubCode1 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 1 Hash" value={data.loanRequest.sourceSubCode1Hash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 2" value={data.loanRequest.sourceSubCode2 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="Source Sub Code 2 Hash" value={data.loanRequest.sourceSubCode2Hash || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField label="Source Sub Code 3" value={data.loanRequest.sourceSubCode3 || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Everflow Transaction Id" value={data.loanRequest.everflowTransactionId || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Referring Site URL" value={data.loanRequest.referringSiteUrl || ''}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Paper>


            <Typography variant="h4" component="h4" align="left">Processing Result</Typography>
            <LoanResultInformation loanResult={data.loanRequest.loanResult}/>

            {data.loanRequest.leadScores.length ? (
                <>
                    <Typography variant="h4" component="h4" align="left">Lead Scores</Typography>
                    <Paper className={classes.paper}>
                        {data.loanRequest.leadScores.map((leadScore, idx) => (
                            <Grid container spacing={2} key={idx.toString()}>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Created At" value={setDate(leadScore.createdAt) || ''} readOnly
                                               fullWidth/>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Grade" value={leadScore.grade || ''} readOnly fullWidth/>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField label="Score" value={leadScore.score || ''} readOnly fullWidth/>
                                </Grid>
                            </Grid>
                        ))}
                    </Paper>
                </>
            ) : null}

            <Typography variant="h4" component="h4" align="left">Performance Summary</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Time to First Campaign Request"
                                   value={data.loanRequest.performanceSummaryData.timeToFirstCampaignHttpRequest + " seconds" || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Total Synchronous Posting Time"
                                   value={data.loanRequest.performanceSummaryData.totalSynchronousPostingTime + " seconds" || ''}
                                   readOnly fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            {data.loanRequest.sourceSwap ? (
                <>
                    <Typography variant="h4" component="h4" align="left">Source Swapper Results</Typography>
                    <Paper className={classes.paper}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Source Swapper"
                                           value={data.loanRequest.sourceSwap.swapperName || ''} readOnly fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Swap Description"
                                           value={data.loanRequest.sourceSwap.description || ''} readOnly fullWidth/>
                            </Grid>
                        </Grid>
                        <br/>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Original Affiliate"
                                           value={data.loanRequest.sourceSwap.originalTrafficSourceAffiliateName || ''}
                                           readOnly fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Original Traffic Source"
                                           value={data.loanRequest.sourceSwap.originalTrafficSourceName || ''} readOnly
                                           fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={2}>
                                <TextField label="Original Promo Id"
                                           value={data.loanRequest.sourceSwap.originalPromoId || ''} readOnly
                                           fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Original Traffic Source Id"
                                           value={data.loanRequest.sourceSwap.originalTrafficSourceId || ''} readOnly
                                           fullWidth/>
                            </Grid>
                        </Grid>
                        <br/>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Final Affiliate"
                                           value={data.loanRequest.sourceSwap.finalTrafficSourceAffiliateName || ''}
                                           readOnly fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Final Traffic Source"
                                           value={data.loanRequest.sourceSwap.finalTrafficSourceName || ''} readOnly
                                           fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={2}>
                                <TextField label="Final Promo Id"
                                           value={data.loanRequest.sourceSwap.finalPromoId || ''} readOnly fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Final Traffic Source Id"
                                           value={data.loanRequest.sourceSwap.finalTrafficSourceId || ''} readOnly
                                           fullWidth/>
                            </Grid>
                        </Grid>
                    </Paper>
                </>
            ) : null}

            {data.loanRequest.payoutStrategySwap ? (
                <>
                    <Typography variant="h4" component="h4" align="left">Payout Strategy Swapper Result</Typography>
                    <Paper className={classes.paper}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Original Strategy"
                                           value={data.loanRequest.payoutStrategySwap.originalStrategy || ''} readOnly
                                           fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Swapped Strategy"
                                           value={data.loanRequest.payoutStrategySwap.swappedStrategy || ''} readOnly
                                           fullWidth/>
                            </Grid>
                            <Grid item xs={12} sm={5} lg={3}>
                                <TextField label="Reason"
                                           value={data.loanRequest.payoutStrategySwap.reason || ''} readOnly fullWidth/>
                            </Grid>
                        </Grid>
                        <br/>
                    </Paper>
                </>
            ) : null}

            <Typography variant="h4" component="h4" align="left" id={"person"}>Person</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="First Name" value={data.loanRequest.firstName || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="Middle Name" value={data.loanRequest.middleName || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={2}>
                        <TextField label="Last Name" value={data.loanRequest.lastName || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={2} lg={1}>
                        <TextField label="Military" value={data.loanRequest.military ? 'TRUE' : 'FALSE'} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={5}>
                        <TextField label="Email" value={data.loanRequest.primaryEmail || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Primary Phone"
                                   value={data.loanRequest.primaryPhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Home Phone"
                                   value={data.loanRequest.homePhone ? data.loanRequest.homePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Mobile Phone"
                                   value={data.loanRequest.mobilePhone ? data.loanRequest.mobilePhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3") : ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Street Address" value={data.loanRequest.homeStreetAddress || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={1}>
                        <TextField label="Line 2" value={data.loanRequest.homeStreetAddress2 || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="City" value={data.loanRequest.homeCity || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={1}>
                        <TextField label="State" value={data.loanRequest.homeState || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={1}>
                        <TextField label="ZIP Code" value={data.loanRequest.homeZip || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Residence Type" value={data.loanRequest.residenceType || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Residence Length"
                                   value={`${data.loanRequest.residenceLengthMonths} months` || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2}>
                        <TextField label="Date of Birth" value={format(data.loanRequest.dateOfBirth, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SensitiveField label="Social Security Number"
                                        value={data.loanRequest.ssn.replace(/(\d{3})(\d{2})(\d{4})/, "$1–$2–$3")}/>
                    </Grid>
                    <Grid item xs={12} sm={8} lg={2}>
                        <SensitiveField label="State ID Number" value={data.loanRequest.stateIdNumber || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="State ID Issued" value={data.loanRequest.stateIdIssued || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <TextField label="State ID Hash" value={data.loanRequest.stateIdNumberHash || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <TextField label="Verified" value={data.loanRequest.verifiedCustomer ? 'TRUE' : 'FALSE'}
                                   readOnly
                                   fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            <Typography variant="h4" component="h4" align="left">Employment Information</Typography>
            <Paper className={classes.paper}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="Type" value={data.loanRequest.employmentType || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="SubType" value={data.loanRequest.employmentSubType || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Employment Length"
                                   value={`${data.loanRequest.employerLengthMonths} months` || ''}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={7} lg={3}>
                        <TextField label="Employer Name" value={data.loanRequest.employerName || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <PhoneField label="Phone" value={data.loanRequest.workPhone || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={5} lg={3}>
                        <TextField label="Ext" value={data.loanRequest.workExt || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CurrencyField label="Monthly Income" value={data.loanRequest.monthlyIncome || ''}/>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField label="Pay Type" value={data.loanRequest.payType || ''} readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Pay Frequency" value={data.loanRequest.payFrequency || ''} readOnly
                                   fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Next Pay Date" value={format(data.loanRequest.nextPayDate, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={2}>
                        <TextField label="Second Pay Date" value={format(data.loanRequest.secondPayDate, 'd MMM yyyy')}
                                   readOnly fullWidth/>
                    </Grid>
                </Grid>
            </Paper>

            <BankInformation
                bankName={data.loanRequest.bankName}
                accountType={data.loanRequest.bankAccountType}
                routingNumber={data.loanRequest.bankRouting}
                accountNumber={data.loanRequest.bankAccount}
                monthsAtBank={data.loanRequest.monthsAtBank}
            />

            <VehicleInformation
                vehicleMake={data.loanRequest.vehicleInfo.vehicleMake}
                vehicleModel={data.loanRequest.vehicleInfo.vehicleModel}
                vehicleTrim={data.loanRequest.vehicleInfo.vehicleTrim}
                vehicleYear={data.loanRequest.vehicleInfo.vehicleYear}
                vehicleMileage={data.loanRequest.vehicleInfo.vehicleMileage}
                titleAsCollateral={data.loanRequest.titleAsCollateral}
                titleClear={data.loanRequest.titleClear}
            />

            <HostedSiteInformation
                loading={hostedSiteDataLoading}
                error={hostedSiteDataError}
                data={hostedSiteData?.hostedSiteData}
            />

            <LoanResponseInformation loanResponse={data.loanRequest.loanResponse}/>

            {data.loanRequest.experiments && data.loanRequest.experiments.length > 0 ? (
                <ExperimentRecords classes={classes} experiments={data.loanRequest.experiments}/>
            ) : null}

            {data.loanRequest.bids && data.loanRequest.bids.length > 0 ? (
                <>
                    <Typography variant="h4" component="h4" align="left">Bid Data</Typography>
                    <Paper className={classes.paper}>
                        <Table className={classes.table} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell variant="head">Bid Created</TableCell>
                                    <TableCell variant="head">Campaign Id</TableCell>
                                    <TableCell variant="head">Bid</TableCell>
                                    <TableCell variant="head">Status</TableCell>
                                    <TableCell variant="head">Expiration</TableCell>
                                    <TableCell variant="head">Lead Id</TableCell>
                                    <TableCell variant="head">Application Id</TableCell>
                                    <TableCell variant="head">Loan / Redeem Request Id</TableCell>
                                    <TableCell variant="head">Redeem Timestamp</TableCell>
                                    <TableCell variant="head">Token</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.loanRequest.bids.map(bid => (
                                    <TableRow key={bid.id}>
                                        <TableCell variant="body">{setDate(bid.originalTimestamp)}</TableCell>
                                        <TableCell variant="body">{bid.campaignId}</TableCell>
                                        <TableCell variant="body">${bid.pricePoint}</TableCell>
                                        <TableCell variant="body">{bid.status}</TableCell>
                                        <TableCell variant="body">{bid.expires}</TableCell>
                                        <TableCell variant="body">{bid.leadId}</TableCell>
                                        <TableCell variant="body">{bid.applicationId}</TableCell>
                                        <TableCell variant="body">
                                            {bid.redeemedRequestId && bid.loanRequestId ? (
                                                    <Fragment>
                                                        <a
                                                            href="/#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                window.open(`/info/leads/${bid.loanRequestId}`)
                                                            }}
                                                        >
                                                            Original
                                                        </a>
                                                        <span style={{padding: "0 10px"}}>|</span>
                                                        <a
                                                            href="/#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                window.open(`/info/leads/${bid.redeemedRequestId}`)
                                                            }}>
                                                            Redeemed
                                                        </a>
                                                    </Fragment>
                                                )
                                                : 'N/A'}
                                        </TableCell>
                                        <TableCell
                                            variant="body">{bid.redeemedTimestamp ? setDate(bid.redeemedTimestamp) : 'N/A'}</TableCell>
                                        <TableCell variant="body">{bid.token}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            ) : null}


            {data.loanRequest.campaignDecisionLog && data.loanRequest.campaignDecisionLog.length > 0 ? (
                <>
                    <Typography variant="h4" component="h4" align="left" id={"campaign-decision-logs"}>Campaign Decision
                        Logs</Typography>
                    <Paper className={classes.paper}>
                        <Table className={classes.table} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell variant="head">Timestamp</TableCell>
                                    <TableCell variant="head">Elapsed</TableCell>
                                    <TableCell variant="head">Servicer</TableCell>
                                    <TableCell variant="head">Campaign</TableCell>
                                    <TableCell variant="head">Type</TableCell>
                                    <TableCell variant="head">Status</TableCell>
                                    <TableCell variant="head">Strategy</TableCell>
                                    <TableCell variant="head">Log Type</TableCell>
                                    <TableCell variant="head">Tier Min</TableCell>
                                    <TableCell variant="head">Price&nbsp;Point</TableCell>
                                    <TableCell variant="head">Billable</TableCell>
                                    <TableCell variant="head">Payable</TableCell>
                                    <TableCell variant="head">Request</TableCell>
                                    <TableCell variant="head">Response</TableCell>
                                    <TableCell variant="head">Result</TableCell>
                                    <TableCell variant="head">Status Code</TableCell>
                                    <TableCell variant="head">Vendor&nbsp;Session&nbsp;Id</TableCell>
                                    <TableCell variant="head">Vendor&nbsp;Message</TableCell>
                                    <TableCell variant="head">Publisher&nbsp;Timeout</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.loanRequest.campaignDecisionLog.map(entry => (
                                    <TableRow key={entry.id}>
                                        <TableCell
                                            variant="body">{entry.requestTimestamp !== null ? setDate(entry.requestTimestamp) : 'null'}</TableCell>
                                        <TableCell variant="body">{entry.elapsedTimeSeconds}s</TableCell>
                                        <TableCell variant="body">{entry.servicer}</TableCell>
                                        <TableCell variant="body">
                                            {entry.campaignId}
                                            {entry.campaignId === 'olp' ? (
                                                <IconButton
                                                    href={`http://secure.tools.partnerweekly.com/application?id=${entry.vendorSessionId}&mode=live`}>
                                                    <LaunchIcon/>
                                                </IconButton>
                                            ) : null}
                                        </TableCell>
                                        <TableCell variant="body">{entry.campaignType} {entry.type}</TableCell>
                                        <TableCell variant="body">{entry.status}</TableCell>
                                        <TableCell variant="body">{entry.strategy}</TableCell>
                                        <TableCell variant="body">{entry.type}</TableCell>
                                        <TableCell variant="body"
                                                   align="right">{entry.tierMin !== null ? convertToCurrency(entry.tierMin) : 'N/A'}</TableCell>
                                        <TableCell variant="body"
                                                   align="right">{entry.pricePoint !== null ? convertToCurrency(entry.pricePoint) : 'N/A'}</TableCell>
                                        <TableCell variant="body"
                                                   align="right">{entry.billable !== null ? convertToCurrency(entry.billable) : 'N/A'}</TableCell>
                                        <TableCell variant="body"
                                                   align="right">{entry.payable !== null ? convertToCurrency(entry.payable) : 'N/A'}</TableCell>
                                        <TableCell variant="body">
                                            <Button onClick={() => openModal({
                                                title: `Request: ${setDate(entry.requestTimestamp)}`,
                                                pre: entry.request
                                            })}>
                                                <LaunchIcon/>
                                            </Button>
                                        </TableCell>
                                        <TableCell variant="body">
                                            <Button onClick={() => openModal({
                                                title: `Response: ${setDate(entry.responseTimestamp)}`,
                                                pre: entry.response,
                                            })}>
                                                <LaunchIcon/>
                                            </Button>
                                        </TableCell>
                                        <TableCell variant="body">
                                            <Button onClick={() => openModal({
                                                title: `Result - ${entry.statusCode}: ${setDate(entry.requestTimestamp)}`,
                                                pre: entry.result
                                            })}>
                                                <LaunchIcon/>
                                            </Button>
                                        </TableCell>
                                        <TableCell variant="body">
                                            {entry.statusCode}
                                        </TableCell>
                                        <TableCell variant="body">{entry.vendorSessionId || 'N/A'}</TableCell>
                                        <TableCell variant="body"
                                                   style={{whiteSpace: "nowrap"}}>{entry.vendorMessage}</TableCell>
                                        <TableCell variant="body">{entry.publisherTimeout ?
                                            <CheckCircleOutlineIcon aria-checked={true}/> :
                                            <RadioButtonUncheckedIcon aria-checked={false}/>}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            ) : null}

            <Typography variant="h4" component="h4" align="left" id={"raw-request-log"}>Raw Request
                Log</Typography>
            {
                loanRequestLogData?.loanRequestLog?.error ?
                    <Alert severity="error">{loanRequestLogData.loanRequestLog.error}</Alert> :
                loanRequestLogData?.loanRequestLog ? (
                <>
                    <Paper className={classes.paper}>
                        <Table className={classes.table} size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell variant="head">URI</TableCell>
                                    <TableCell variant="head">Status Code</TableCell>
                                    <TableCell variant="head">Message</TableCell>
                                    <TableCell variant="head">View Raw Data</TableCell>
                                    <TableCell variant="head">OLP Session</TableCell>
                                    <TableCell variant="head">OLP Decision</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell variant="body">{loanRequestLogData.loanRequestLog.uri}</TableCell>
                                    <TableCell variant="body">{loanRequestLogData.loanRequestLog.statusCode === 200 ?
                                        <CheckBoxIcon style={{color: theme.palette.success.main}}/> :
                                        <WarningIcon style={{color: theme.palette.error.main}}/>}</TableCell>
                                    <TableCell
                                        variant="body">{loanRequestLogData.loanRequestLog.message.length ? loanRequestLogData.loanRequestLog.message : 'N/A'}</TableCell>
                                    <TableCell variant="body">
                                        <IconButton
                                            onClick={() => handleOpen(loanRequestLogData.loanRequestLog.request, loanRequestLogData.loanRequestLog.response)}>
                                            <LaunchIcon/>
                                        </IconButton>
                                    </TableCell>
                                    <TableCell variant="body">
                                        <CreateOlpLink olpSessionId={loanRequestLogData.loanRequestLog.olpSessionId}/>
                                    </TableCell>
                                    <TableCell
                                        variant="body">{loanRequestLogData.loanRequestLog.olpDecision || 'N/A'}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Paper>
                    <Modal
                        open={isLogOpen}
                        onClose={handleClose}
                    >
                        <Paper className={classes.modal}>
                            <Tabs
                                aria-label="raw data tabs"
                                value={selectedTab}
                                onChange={handleTabClick}
                                indicatorColor="primary"
                                textColor="primary"
                                centered
                            >
                                <Tab
                                    label="Request Raw Tab"
                                    id="raw-data-0"
                                />
                                <Tab
                                    label="Response Raw Tab"
                                    id="request-data-0"
                                />
                            </Tabs>
                            <Box
                                component="div"
                                role="tabpanel"
                                hidden={selectedTab !== 0}
                                className={boxClasses.root}
                            >
                                {modalData.request ? (
                                    <ParseResponse data={modalData.request}/>
                                ) : null}
                            </Box>
                            <Box
                                component="div"
                                role="tabpanel"
                                hidden={selectedTab !== 1}
                                className={boxClasses.root}
                            >
                                {modalData.response ? (
                                    <ParseResponse data={modalData.response}/>
                                ) : null}
                            </Box>
                        </Paper>
                    </Modal>
                </>
            ) : loanRequestLogLoading ?
                <CircularProgress color="inherit"/>
                : (
                    <>
                        {
                            loanRequestLogError || (loanRequestLogData && !loanRequestLogData.loanRequestLog) ?
                                <Alert severity="error">
                                    Something went wrong. Try again in a few minutes
                                </Alert> : null
                        }
                        <Button variant="contained" color="primary" onClick={onClickLoadLog}
                                style={{display: 'flex', marginTop: 30, marginBottom: 30}}>
                            Load Loan Request Log
                        </Button>
                    </>
                )
            }

            {
                loadComponents ? (
                    <ErrorBoundary>
                        <Suspense fallback={<span></span>}>
                            <CampaignFilterResultsTable id={id}/>
                            <LoanRedirectDataTable id={id}/>
                            <MarketingRedirectTable id={id}/>
                        </Suspense>
                    </ErrorBoundary>
                ) : null
            }

            {
                data.loanRequest.campaignMinPrice ? (
                    <>
                        <Typography variant="h4" component="h4" align="left" id={"campaign-min-data"}>Campaign Min
                            Data</Typography>
                        <Paper className={classes.paper}>
                            <Table className={classes.table} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell variant="head">Campaign Id</TableCell>
                                        <TableCell variant="head">Min Price</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.loanRequest.campaignMinPrice.map(minPrice => (
                                        <TableRow key={minPrice.id}>
                                            <TableCell variant="body">{minPrice.campaignId}</TableCell>
                                            <TableCell variant="body">{convertToCurrency(minPrice.minPrice)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    </>
                ) : null
            }

            <IdentifyEventsTable id={id}/>

            <SignatureBlocksTable
                id={id}
                signatureBlockActions={data.loanRequest.blockActions}/>

            {
                loadComponents ? (
                    <ErrorBoundary>
                        <Suspense fallback={<span></span>}>
                            <CloudwatchLogsTable id={id}/>
                        </Suspense>
                    </ErrorBoundary>
                ) : null
            }

            <LeadNavigationMenu
                hasCampaignDescisionLogs={data.loanRequest.campaignDecisionLog.length}
                hasRawRequestLogs={false}
                hasLoanRedirect={data.loanRequest.loanRedirect.length}
                hasCampaignMinPrice={data.loanRequest.campaignMinPrice}
            />

            <Modal
                open={isModalOpen}
                onClose={closeModal}
            >
                <Paper className={classes.modal}>
                    <h2>{modalData.title}</h2>

                    {modalData.pre ? (
                        <Box className={boxClasses.root}>
                            <ParseResponse data={modalData.pre}/>
                        </Box>
                    ) : (
                        <Alert severity="error">
                            Well, this in embarrassing&hellip; something went wrong.
                        </Alert>
                    )}
                </Paper>
            </Modal>
        </>
    )
        ;
}

export default Lead
